import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { BookOpen, LoaderCircle } from "lucide-react";
import { useOptionalUser } from "@/components/UserProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { availableSkillsQueryOptions } from "@/lib/data/react-query/skills";

export function ChatSkillPicker({
  open,
  onOpenChange,
  onSelect,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (nativeName: string) => void;
}) {
  const [search, setSearch] = useState("");
  const user = useOptionalUser();
  const canManage =
    user?.hasPagePermission("skills.firm") ||
    user?.hasPagePermission("skills.personal");
  const skills = useInfiniteQuery({
    ...availableSkillsQueryOptions(search),
    enabled: open,
  });
  const items = skills.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[80vh] flex-col sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Use a skill</DialogTitle>
          <DialogDescription>
            Choose reusable instructions for this message. LawWorks can also use
            a matching skill automatically.
          </DialogDescription>
        </DialogHeader>
        <Input
          aria-label="Find a skill"
          placeholder="Find a skill…"
          maxLength={200}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
        <div className="min-h-0 space-y-2 overflow-y-auto">
          {skills.isPending && (
            <p
              className="text-muted-foreground flex items-center gap-2 py-4 text-sm"
              role="status"
            >
              <LoaderCircle className="size-4 animate-spin" /> Loading skills…
            </p>
          )}
          {skills.isError && (
            <div role="alert" className="space-y-2 py-3 text-sm">
              <p>Skills could not be loaded.</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void skills.refetch()}
              >
                Try again
              </Button>
            </div>
          )}
          {!skills.isPending && !skills.isError && !items.length && (
            <p className="text-muted-foreground py-4 text-sm">
              {search
                ? "No skills match your search."
                : "No skills are available yet. Enabled firm skills and your personal skills appear here."}
            </p>
          )}
          {items.map((skill) => (
            <button
              type="button"
              key={skill.id}
              className="hover:bg-muted focus-visible:ring-ring flex w-full items-start gap-3 rounded-lg border p-3 text-left outline-none focus-visible:ring-2"
              onClick={() => {
                onSelect(skill.nativeName);
                onOpenChange(false);
              }}
            >
              <BookOpen className="text-muted-foreground mt-0.5 size-4 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium">{skill.name}</span>
                <span className="text-muted-foreground mt-1 line-clamp-2 block text-sm">
                  {skill.description}
                </span>
              </span>
              <span className="text-muted-foreground shrink-0 text-xs">
                {skill.scope === "firm" ? "Firm" : "Personal"}
              </span>
            </button>
          ))}
          {skills.hasNextPage && (
            <Button
              variant="ghost"
              className="w-full"
              disabled={skills.isFetchingNextPage}
              onClick={() => void skills.fetchNextPage()}
            >
              {skills.isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          )}
        </div>
        {canManage && (
          <a
            href="/skills"
            className="text-muted-foreground hover:text-foreground text-sm underline underline-offset-4"
          >
            Manage skills
          </a>
        )}
      </DialogContent>
    </Dialog>
  );
}
